/**
 * AdPilot API service — one function per backend endpoint so page components
 * never hand-build URLs or query strings.
 *
 * B2C self-serve: every call is scoped by userId + the user's connection.
 * There is deliberately NO accountId anywhere in this surface.
 */
import { api } from './apiConfig';
import { friendlyCampaignError } from '../utils/campaignErrors';
export const listShoppingCatalog = async ({ connectionId }) => (await api.get('/api/v3/adpilot/google-ads/shopping-catalog', { params: { connectionId } })).data;

export const loginWithGoogle = async (credential, invitationCode) => {
  const { data } = await api.post('/api/v3/adpilot/login', {
    credential,
    ...(invitationCode ? { invitationCode } : {}),
  });
  return data;
};

export const trackInvitationOpen = async (invitationCode) => {
  await api.post('/api/v3/adpilot/invitations/open', { invitationCode });
};

export const getCommunityProof = async () => {
  const { data } = await api.get('/api/v3/adpilot/community-proof');
  return data;
};

export const prepareConnection = async ({ userId, provider }) => {
  const { data } = await api.post('/api/v3/adpilot/connect', { userId, ...(provider ? { provider } : {}) });
  return data;
};

export const getMetaAccessRequest = async () => {
  const { data } = await api.get('/api/v3/adpilot/facebook/access-request');
  return data.request;
};

export const submitMetaAccessRequest = async (profileLink) => {
  const { data } = await api.post('/api/v3/adpilot/facebook/access-request', { profileLink });
  return data.request;
};

export const connectChatGptAds = async ({ userId, apiKey }) => {
  const { data } = await api.post('/api/v3/adpilot/connect', {
    userId, provider: 'chatgpt-ads', apiKey,
  });
  return data;
};

export const getConnection = async ({ userId, provider }) => {
  const { data } = await api.get('/api/v3/adpilot/connection', {
    params: { userId, ...(provider ? { provider } : {}) },
  });
  return data;
};

export const activateConnection = async ({ userId, connectionId, adAccountId }) => {
  const { data } = await api.put('/api/v3/adpilot/connection', {
    userId, connectionId, adAccountId,
  });
  return data;
};

export const listAdAccounts = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/facebook/ad-accounts', {
    params: { userId, connectionId },
  });
  return data.ad_accounts;
};

export const listGoogleAdsCustomers = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/google-ads/customers', {
    params: { userId, connectionId },
  });
  return data.customers;
};

export const listGoogleAdsCampaigns = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/google-ads/campaigns', {
    params: { userId, connectionId },
  });
  return data.campaigns;
};

export const listFacebookCampaigns = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/facebook/campaigns', {
    params: { userId, connectionId },
  });
  return data.campaigns;
};

export const listChatGptAdsCampaigns = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/chatgpt-ads/campaigns', {
    params: { userId, connectionId },
  });
  return data.campaigns;
};

export const listFacebookPages = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/facebook/pages', {
    params: { userId, connectionId },
  });
  return data.pages;
};

export const listMetaLeadAssets = async ({ connectionId, pageId }) => {
  const { data } = await api.get('/api/v3/adpilot/facebook/lead-assets', { params: { connectionId, pageId } });
  return data;
};

export const listMetaConversionAssets = async ({ connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/facebook/conversion-assets', { params: { connectionId } });
  return data;
};

export const searchFacebookLocations = async ({ userId, connectionId, query }) => {
  const { data } = await api.get('/api/v3/adpilot/facebook/locations', {
    params: { userId, connectionId, q: query },
  });
  return data.locations;
};

export const uploadCreativeZip = async ({ connectionId, file }) => {
  const form = new FormData();
  form.append('connectionId', connectionId);
  form.append('file', file);
  const { data } = await api.post('/api/v3/adpilot/creatives', form);
  return data.creative;
};

export const draftFacebookCampaign = async ({ userId, connectionId, brief, dailyBudget, durationDays, locations, specialAdCategory, clarificationAnswer }) => {
  const { data } = await api.post('/api/v3/adpilot/campaigns/draft', {
    userId, connectionId, brief, dailyBudget, durationDays, locations, specialAdCategory,
    ...(clarificationAnswer ? { clarificationAnswer } : {}),
  });
  return data;
};

export const publishFacebookCampaign = async ({ userId, draftId, draft, pageId, replaceCampaignId }) => {
  const { data } = await api.post(`/api/v3/adpilot/campaigns/draft/${draftId}/publish`, {
    userId,
    draft,
    ...(pageId ? { pageId } : {}),
    ...(replaceCampaignId ? { replaceCampaignId } : {}),
  });
  return data;
};

export const draftAiCampaign = async ({ userId, connectionId, ...brief }) => {
  const { data } = await api.post('/api/v3/adpilot/ai-campaigns/draft', {
    userId, connectionId, ...brief,
  });
  return data;
};

export const getAiCampaignDraft = async ({ userId, draftId }) => {
  const { data } = await api.get(`/api/v3/adpilot/ai-campaigns/draft/${draftId}`, {
    params: { userId },
  });
  return data;
};

export const publishAiCampaign = async ({ userId, draftId, pageId }) => {
  const { data } = await api.post(`/api/v3/adpilot/ai-campaigns/draft/${draftId}/publish`, {
    userId, ...(pageId ? { pageId } : {}),
  });
  return data;
};

// Import and monitoring are separate server-side concerns (see routes/v3/
// adpilot.js POST /campaigns/import):
//   { campaignIds }               → import + WATCH those ids (≤5 watched total)
//   { campaignIds, watch: false } → metadata sync only, unlimited, watch state preserved
//   { all: true }                 → import every campaign on the ad account, unwatched
export const importCampaigns = async ({ userId, connectionId, campaignIds, all, watch }) => {
  const { data } = await api.post('/api/v3/adpilot/campaigns/import', {
    userId,
    connectionId,
    ...(all ? { all: true } : { campaignIds }),
    ...(watch === false ? { watch: false } : {}),
  });
  return data;
};

export const getAnalysisQuota = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/analysis-quota', {
    params: { userId, connectionId },
  });
  return data;
};

// Default scope: the WATCHED working set only (CreateCampaignPage counts these
// rows against the monitoring limit). scope: 'all' adds imported-but-unwatched
// campaigns, watched first; every row carries a `watched` boolean either way.
export const listImportedCampaigns = async ({ userId, connectionId, scope, includeSync = false }) => {
  const { data } = await api.get('/api/v3/adpilot/campaigns', {
    params: { userId, connectionId, ...(scope ? { scope } : {}) },
  });
  return includeSync ? data : data.campaigns;
};

export const refreshMetaSnapshot = async ({ userId, connectionId }) => {
  const { data } = await api.post('/api/v3/adpilot/facebook/snapshot/refresh', { userId, connectionId });
  return data;
};

export const getMetaEditContext = async ({ connectionId, campaignId, recommendationId }) => {
  const { data } = await api.get(`/api/v3/adpilot/facebook/campaigns/${encodeURIComponent(campaignId)}/edit-context`, { params: { connectionId, recommendationId } });
  return data;
};
export const proposeMetaEdit = async ({ connectionId, campaignId, ...proposal }) => {
  const { data } = await api.post(`/api/v3/adpilot/facebook/campaigns/${encodeURIComponent(campaignId)}/edits`, { connectionId, ...proposal });
  return data;
};
export const decideMetaEdit = async ({ connectionId, editId, action }) => {
  const { data } = await api.post(`/api/v3/adpilot/facebook/edits/${encodeURIComponent(editId)}/${action}`, { connectionId });
  return data;
};

export const stopWatchingCampaign = async ({ userId, connectionId, platformCampaignId }) => {
  await api.delete(`/api/v3/adpilot/campaigns/${encodeURIComponent(platformCampaignId)}`, {
    params: { userId, connectionId },
  });
};

export const listRecommendations = async ({ userId, connectionId, status = 'proposed' }) => {
  const { data } = await api.get('/api/v3/adpilot/recommendations', {
    params: { userId, connectionId, status },
  });
  return data.recommendations;
};

export const dismissRecommendation = async ({ userId, connectionId, recommendationId }) => {
  const { data } = await api.post(`/api/v3/adpilot/recommendations/${recommendationId}/dismiss`, {
    userId, connectionId,
  });
  return data;
};

export const applyRecommendations = async ({ userId, connectionId, recommendationIds }) => {
  const { data } = await api.post('/api/v3/adpilot/recommendations/apply', {
    userId, connectionId, recommendationIds,
  });
  return data;
};

export const getActivity = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/activity', {
    params: { userId, connectionId },
  });
  return data;
};

export const undoApply = async ({ userId, connectionId, applyId }) => {
  const { data } = await api.post(`/api/v3/adpilot/applies/${applyId}/undo`, { userId, connectionId });
  return data;
};

export const getCampaignReport = async ({ userId, connectionId, ...options }) => {
  const response = await api.get('/api/v3/adpilot/reporting', { params: { userId, connectionId, ...options } });
  return response.data;
};

export const getPerformance = async ({ userId, connectionId }) => {
  const { data } = await api.get('/api/v3/adpilot/performance', {
    params: { userId, connectionId },
  });
  return data;
};

export const runDailyNow = async ({ userId, connectionId, lookbackDays }) => {
  const body = { userId, connectionId };
  if (lookbackDays != null) body.lookbackDays = lookbackDays;
  const { data } = await api.post('/api/v3/adpilot/run-daily', body);
  return data;
};

export const getAdminOverview = async () => {
  const { data } = await api.get('/api/v3/adpilot/admin/overview');
  return data;
};

export const issueInvitation = async ({ label, expiresInDays }) => {
  const { data } = await api.post('/api/v3/adpilot/admin/invitations', { label, expiresInDays });
  return data;
};

export const shareInvitation = async (id) => {
  const { data } = await api.post(`/api/v3/adpilot/admin/invitations/${id}/share`, {});
  return data;
};

export const updateInvitation = async ({ id, label, expiresInDays }) => {
  const { data } = await api.patch(`/api/v3/adpilot/admin/invitations/${id}`, { label, expiresInDays });
  return data;
};

export const revokeInvitation = async (id) => {
  await api.delete(`/api/v3/adpilot/admin/invitations/${id}`);
};

export const getReferrals = async () => {
  const { data } = await api.get('/api/v3/adpilot/referrals');
  return data;
};

export const createReferral = async () => {
  const { data } = await api.post('/api/v3/adpilot/referrals', {});
  return data;
};

export const getWorkspace = async () => {
  const { data } = await api.get('/api/v3/adpilot/workspace');
  return data;
};

export const updateWorkspace = async ({ industry, businessSize }) => {
  const { data } = await api.put('/api/v3/adpilot/workspace', { industry, businessSize });
  return data;
};

export const inviteWorkspaceMember = async ({ label, role }) => {
  const { data } = await api.post('/api/v3/adpilot/workspace/invitations', { label, role });
  return data;
};

export const removeWorkspaceMember = async (memberId) => {
  await api.delete(`/api/v3/adpilot/workspace/members/${memberId}`);
};

export const disconnectConnection = async ({ userId, provider }) => {
  const { data } = await api.post('/api/v3/adpilot/connection/disconnect', { userId, provider });
  return data;
};

export const provisionGoogleAds = async ({ userId, businessName, currency, timeZone, email }) => {
  const { data } = await api.post('/api/v3/adpilot/google-ads/provision', { userId, businessName, currency, timeZone, email });
  return data;
};

export const resendGoogleAdsInvite = async ({ userId, email }) => {
  const { data } = await api.post('/api/v3/adpilot/google-ads/provision/resend-invite', { userId, email });
  return data;
};

export const acknowledgeGoogleAdsSetup = async ({ userId }) => {
  const { data } = await api.post('/api/v3/adpilot/google-ads/provision/acknowledge', { userId });
  return data;
};

export function apiErrorMessage(error, fallback = 'Request failed') {
  return error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
}

export function campaignErrorMessage(error, fallback = 'We couldn’t complete this request') {
  return friendlyCampaignError(error, fallback);
}

export const updateCampaignStatus = async ({ userId, connectionId, platformCampaignId, status, expectedStatus }) => {
  const { data } = await api.put(`/api/v3/adpilot/campaigns/${encodeURIComponent(platformCampaignId)}/status`, {
    userId, connectionId, status, expectedStatus,
  });
  return data;
};

export const checkCreativeAssignment = async draftId => (await api.post(`/api/v3/adpilot/ai-campaigns/draft/${draftId}/creatives/reconcile`)).data;
export const reviewCreativeRetry = async draftId => (await api.post(`/api/v3/adpilot/ai-campaigns/draft/${draftId}/creatives/retry-review`)).data;
export const applyCreativeRetry = async (draftId, reviewId) => (await api.post(`/api/v3/adpilot/ai-campaigns/draft/${draftId}/creatives/retry-reviews/${reviewId}/apply`)).data;

export const getGoogleBudgetContext = async ({ connectionId, campaignId, recommendationId }) => {
  const { data } = await api.get(`/api/v3/adpilot/google-ads/campaigns/${encodeURIComponent(campaignId)}/budget-context`, { params: { connectionId, recommendationId } });
  return data;
};
export const proposeGoogleBudgetEdit = async ({ connectionId, campaignId, ...proposal }) => {
  const { data } = await api.post(`/api/v3/adpilot/google-ads/campaigns/${encodeURIComponent(campaignId)}/budget-edits`, { connectionId, ...proposal });
  return data;
};
export const decideGoogleBudgetEdit = async ({ connectionId, editId, action }) => {
  const { data } = await api.post(`/api/v3/adpilot/google-ads/budget-edits/${encodeURIComponent(editId)}/${action}`, { connectionId });
  return data;
};
